import type React from "react";
import { createContext, useEffect, useRef, useState } from "react";
import type {
  TaskCreateCommand,
  TaskDeleteCommand,
  TaskUpdateCommand,
} from "shared/model/board/board.commands";
import type {
  ActiveBoardUser,
  BoardSyncEvent,
  TaskCreatedEvent,
  TaskDeletedEvent,
  TaskUpdatedEvent,
} from "shared/model/board/board.events";
import type { BoardResponse } from "shared/model/board/board.response";
import type { TeamResponse } from "shared/model/team/team.response";
import type { UserResponse } from "shared/model/user/user.response";
import io, { type Socket } from "socket.io-client";
import { toast } from "sonner";
import { BoardService } from "@/api/Board.service";
import { TeamService } from "@/api/Team.service";
import { UserService } from "@/api/User.service";

interface BoardContextParams {
  teamId: string;
}

interface BoardFilters {
  showOnlyMyTasks: boolean;
}

interface BoardContext {
  teamId: string;
  board: BoardResponse | null;
  team: TeamResponse | null;
  teamUsers: UserResponse[];
  activeUsers: ActiveBoardUser[];
  moveTask: (taskId: string, targetColumnId: string) => void;
  createTask: (
    taskId: string,
    text: string,
    ownerId: string | null,
    columnId: string,
  ) => void;
  updateTask: (taskId: string, newOwnerId: string | null, text: string) => void;
  deleteTask: (taskId: string) => void;
  filters: BoardFilters;
  setFilters: (filters: BoardFilters) => void;
}

export const BoardContext = createContext<BoardContext>({
  teamId: "",
  board: null,
  team: null,
  teamUsers: [],
  activeUsers: [],
  moveTask: () => {},
  createTask: () => {},
  updateTask: () => {},
  deleteTask: () => {},
  filters: {
    showOnlyMyTasks: false,
  },
  setFilters: () => {},
});

export const BoardContextProvider: React.FC<
  React.PropsWithChildren<BoardContextParams>
> = ({ children, teamId }) => {
  const socket = useRef<Socket>(undefined);
  const [board, setBoard] = useState<BoardResponse | null>(null);
  const [team, setTeam] = useState<TeamResponse | null>(null);
  const [teamUsers, setTeamUsers] = useState<UserResponse[]>([]);
  const [activeUsers, setActiveUsers] = useState<ActiveBoardUser[]>([]);

  const [filters, setFilters] = useState<BoardFilters>({
    showOnlyMyTasks: false,
  });

  useEffect(() => {
    BoardService.getBoard(teamId).then((board) => setBoard(board));

    TeamService.getTeamById(teamId).then((team) => setTeam(team));

    UserService.getUsersByTeamId(teamId)
      .then((users) => setTeamUsers(users))
      .catch(console.log);
  }, [teamId]);

  useEffect(() => {
    const createdSocket = io(`${process.env.RETRO_WEB_SOCKET_URL}/board`, {
      query: {
        team_id: teamId,
      },
      extraHeaders: {
        //@ts-expect-error
        Authorization: window.localStorage.getItem("Bearer"),
      },
      reconnection: true,
      reconnectionAttempts: Number.POSITIVE_INFINITY,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 8000,
      randomizationFactor: 0.5,
    });
    socket.current = createdSocket;

    createdSocket.on("task_created_event", (event: TaskCreatedEvent) => {
      setBoard((board) =>
        board
          ? {
              ...board,
              tasks: [
                ...board.tasks,
                {
                  id: event.taskId,
                  columnId: event.columnId,
                  ownerId: event.ownerId,
                  text: event.text,
                  createdAt: event.createdAt,
                  updatedAt: event.updatedAt,
                },
              ],
            }
          : null,
      );
    });

    createdSocket.on("task_updated_event", (event: TaskUpdatedEvent) => {
      setBoard((board) =>
        board
          ? {
              ...board,
              tasks: board.tasks.map((task) => {
                if (task.id === event.taskId) {
                  task.columnId = event.columnId;
                  task.ownerId = event.ownerId;
                  task.text = event.text;
                  task.createdAt = event.createdAt;
                  task.updatedAt = event.updatedAt;
                }

                return task;
              }),
            }
          : null,
      );
    });

    createdSocket.on("task_deleted_event", (event: TaskDeletedEvent) => {
      setBoard((board) =>
        board
          ? {
              ...board,
              tasks: board.tasks.filter((task) => task.id !== event.taskId),
            }
          : null,
      );
    });

    createdSocket.on("event_board_sync", (event: BoardSyncEvent) => {
      setActiveUsers(event.users);
    });

    createdSocket.on("error", (e) => {
      console.log(e);
      toast.error("Wystąpił błąd");
    });

    return () => {
      createdSocket.off();
      createdSocket.disconnect();
    };
  }, []);

  const moveTask = (taskId: string, targetColumnId: string) => {
    const command: TaskUpdateCommand = {
      taskId: taskId,
      columnId: targetColumnId,
    };

    socket.current?.emit("command_update_task", command);
  };

  const createTask = (
    taskId: string,
    text: string,
    ownerId: string | null,
    columnId: string,
  ) => {
    const command: TaskCreateCommand = {
      taskId: taskId,
      ownerId: ownerId,
      text: text,
      columnId: columnId,
    };

    socket.current?.emit("command_create_task", command);
  };

  const updateTask = (
    taskId: string,
    newOwnerId: string | null,
    text: string,
  ) => {
    const command: TaskUpdateCommand = {
      taskId: taskId,
      ownerId: newOwnerId,
      text: text,
    };

    const task = board?.tasks.find((task) => task.id === taskId);
    if (task) {
      task.ownerId = newOwnerId;
      task.text = text;
    }

    socket.current?.emit("command_update_task", command);
  };

  const deleteTask = (taskId: string) => {
    const command: TaskDeleteCommand = {
      taskId: taskId,
    };

    socket.current?.emit("command_delete_task", command);
  };

  return (
    <BoardContext.Provider
      value={{
        teamId: teamId,
        board: board,
        team: team,
        teamUsers: teamUsers,
        activeUsers: activeUsers,
        moveTask: moveTask,
        createTask: createTask,
        updateTask: updateTask,
        deleteTask: deleteTask,
        filters: filters,
        setFilters: setFilters,
      }}
    >
      {children}
    </BoardContext.Provider>
  );
};
